/**
 * useContractListKpis - Hook para buscar KPIs totalizadores da listagem de contratos gerados
 * 
 * Consome a RPC `get_contract_list_kpis` que retorna contagens por status
 * respeitando permissões de ownership e filtros ativos da listagem.
 * 
 * @version 1.1.0
 * RELEASE: 2026-04-09 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-09 v1.1.0 - Aceita filtros da listagem para sincronizar KPIs
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ContractFilters } from '@/hooks/useContractsPaginated';

/** Logger do módulo */
const logger = createLogger('useContractListKpis');

/** Estrutura dos KPIs retornados pela RPC */
export interface ContractListKpis {
  total_contracts: number;
  created_today: number;
  /** Contratos com status 'generated' (PDF gerado) */
  pdf_generated: number;
  pending_signature: number;
  signed: number;
}

/** Valores padrão (zerados) */
const EMPTY_KPIS: ContractListKpis = {
  total_contracts: 0,
  created_today: 0,
  pdf_generated: 0,
  pending_signature: 0,
  signed: 0,
};

/**
 * Hook para buscar KPIs totalizadores da listagem de contratos gerados
 * Retorna contagens por status com permissões de ownership e filtros aplicados
 * 
 * @param filters - Filtros ativos da listagem (workspace, status, vendedor, etc.)
 */
export function useContractListKpis(filters?: ContractFilters) {
  const { currentTenant } = useAuth();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contract-list-kpis', currentTenant?.id, filters],
    queryFn: async () => {
      logger.debug('Buscando KPIs da listagem de contratos', { filters });

      const { data, error } = await supabase.rpc('get_contract_list_kpis', {
        p_tenant_hint: currentTenant?.id || null,
        p_workspace_id: filters?.workspace_id || null,
        p_status: filters?.status || null,
        p_search: filters?.search || null,
        p_seller_id: filters?.seller_id || null,
        p_date_field: filters?.date_field || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        /** v1.1.0: Novos filtros para sincronizar KPIs com listagem */
        p_signer_role: filters?.signer_role || null,
        p_published_contract_id: filters?.published_contract_id || null,
        p_pending_signer_role: filters?.pending_signer_role || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar KPIs de contratos', error);
        throw error;
      }

      // RPC retorna array com 1 linha
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return EMPTY_KPIS;

      return {
        total_contracts: Number(row.total_contracts) || 0,
        created_today: Number(row.created_today) || 0,
        /** Mapeia alias 'sent' da RPC para campo 'pdf_generated' */
        pdf_generated: Number(row.sent) || 0,
        pending_signature: Number(row.pending_signature) || 0,
        signed: Number(row.signed) || 0,
      } as ContractListKpis;
    },
    enabled: !!currentTenant?.id,
    staleTime: 15_000,
    gcTime: 2 * 60_000,
  });

  return {
    kpis: data || EMPTY_KPIS,
    isLoading,
    refetch,
  };
}
